import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  bookKey,
  getBook,
  getCharacters,
  getBookProblems,
  getEditionPlan,
  getSearchStatus,
  listVoices,
} from "../api";
import type { Messages } from "../i18n";
import { narrationSummary } from "./NarrationSettings";

/**
 * BookTools is the list of rows that lead to the book's other screens, each
 * with its state in a line so the page says what needs doing without opening
 * anything: an edition behind, a cast not yet extracted.
 *
 * It sits above the chapter list (BookPage), because that line is the whole
 * point of the rows and a chapter list is unbounded. Rows and not chips for
 * the same reason — a chip has nowhere to put the line.
 */
export default function BookTools({
  bookID,
  t,
  canPublish,
  canCharacters,
  canCover,
  canSettings,
  canNarrate,
}: {
  bookID: string;
  t: Messages;
  canPublish: boolean;
  canCharacters: boolean;
  canCover: boolean;
  canSettings: boolean;
  canNarrate: boolean;
}) {
  const plan = useQuery({
    queryKey: ["edition", bookID],
    queryFn: () => getEditionPlan(bookID),
    enabled: canPublish,
    staleTime: 0,
  });
  const characters = useQuery({
    queryKey: ["characters", bookID, "draft"],
    queryFn: () => getCharacters(bookID),
    enabled: canCharacters,
    retry: false,
  });
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    enabled: canSettings,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  // Passages with no audio (ADR-0120). The row exists only when there are
  // some: it is something to do, not a setting to look at.
  const problems = useQuery({
    queryKey: ["narration-problems", bookID],
    queryFn: () => getBookProblems(bookID),
    enabled: canNarrate,
    retry: false,
    staleTime: 0,
  });
  const search = useQuery({
    queryKey: ["search", bookID],
    queryFn: () => getSearchStatus(bookID),
    enabled: canSettings,
    retry: false,
  });

  // The cover counts as a change to publish, as the cast does (ADR-0062); a
  // chapter's pictures are already in `changes`.
  const pendingChanges = plan.data
    ? plan.data.changes.length +
      (plan.data.characters_changed ? 1 : 0) +
      (plan.data.cover_changed ? 1 : 0)
    : 0;
  const publishLine = plan.data
    ? plan.data.published
      ? t.studio.toolPublishEdition(plan.data.published.number, pendingChanges)
      : plan.data.changes.length > 0
        ? t.studio.toolPublishFirst(plan.data.changes.length)
        : t.studio.toolPublishNone
    : "";
  const charactersStale =
    !!characters.data && characters.data.characters.length > 0 && characters.data.stale > 0;
  const charactersLine = characters.data
    ? characters.data.characters.length > 0
      ? charactersStale
        ? `${t.studio.toolCharactersCount(characters.data.characters.length)} · ${t.studio.toolCharactersStale(characters.data.stale)}`
        : t.studio.toolCharactersCount(characters.data.characters.length)
      : t.studio.toolCharactersNone
    : "";
  const searchStale = !!search.data?.grounded && (search.data?.stale ?? 0) > 0;
  const settingsLine = book.data
    ? [
        narrationSummary(book.data, catalogue.data, t),
        search.data
          ? search.data.grounded
            ? searchStale
              ? `${t.studio.toolSettingsSearchOn}, ${t.studio.toolSearchStale(search.data.stale)}`
              : t.studio.toolSettingsSearchOn
            : t.studio.toolSettingsSearchOff
          : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const rows: { tool: string; line: string; attention?: boolean }[] = [];
  const missing = problems.data ?? [];
  if (missing.length > 0) {
    rows.push({
      tool: "narration",
      line: t.narration.problemsLine(
        missing.length,
        new Set(missing.map((p) => p.chapter_id)).size,
      ),
      attention: true,
    });
  }
  if (canPublish) {
    rows.push({ tool: "publish", line: publishLine, attention: pendingChanges > 0 });
  }
  if (canCharacters)
    rows.push({ tool: "characters", line: charactersLine, attention: charactersStale });
  if (canCover) {
    rows.push({
      tool: "cover",
      line: book.data?.cover_url ? t.studio.toolCoverSet : t.studio.toolCoverNone,
    });
  }
  if (canSettings) rows.push({ tool: "settings", line: settingsLine, attention: searchStale });
  if (rows.length === 0) return null;

  return (
    <section className="mb-8" data-testid="book-tools">
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">
        {t.book.tools}
      </h2>
      <ul className="divide-y divide-line rounded-card border border-line bg-surface">
        {rows.map((row) => (
          <li key={row.tool}>
            <Link
              to={`/studio/books/${bookID}/${row.tool}`}
              data-testid={`tool-${row.tool}`}
              className="flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-sunk"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t.studio.tools[row.tool]}</span>
                {/* The line's space is reserved while it loads, never added
                    when it arrives. Each of these lines comes from its own
                    query, so rendering them only once they resolve made this
                    block grow four times in the second after the page opened —
                    and it sits above the chapter list and the Add chapter
                    button, which then moved out from under a thumb already on
                    its way down. `\u00a0` rather than a height class: it is the
                    same line box as the real text, whatever the font does. */}
                <span
                  className={`mt-0.5 block truncate text-xs ${row.attention ? "text-amber-700 dark:text-amber-400" : "text-muted"}`}
                  dir="auto"
                >
                  {row.line || "\u00a0"}
                </span>
              </span>
              <svg
                viewBox="0 0 24 24"
                className="size-4 shrink-0 text-muted rtl:-scale-x-100"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m9 6 6 6-6 6" />
              </svg>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
